import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import * as api from "@/api/supply";
import { useUrlParam } from "@/lib/url-state";
import { cn } from "@/lib/utils";
import { SUPPLY_UI } from "./features";
import { SupplyCommitmentsPage } from "./SupplyCommitmentsPage";
import { OzonSuppliesPage } from "./ozon/OzonSuppliesPage";
import { RUNS_KEY } from "./ozon/ozonQueries";
import { SupplyDistributionPage } from "./SupplyDistributionPage";
import { SupplyFbsPage } from "./SupplyFbsPage";
import { SupplyProductionPage } from "./SupplyProductionPage";
import { SupplyShipmentsPage } from "./SupplyShipmentsPage";

type Tab = "distribution" | "production" | "shipments" | "commitments" | "ozon" | "fbs";

const TABS: Tab[] = ["distribution", "production", "shipments", "commitments", "ozon", "fbs"];

export function SupplyPage(): React.ReactElement {
  // ⚠️ Вкладка живёт в АДРЕСЕ, а не в состоянии: иначе раздел нельзя ни прислать ссылкой, ни
  // снять харнессом скриншотов — а по соглашению проекта фичевая страница обязана открываться
  // напрямую по URL. Заодно не теряется при перезагрузке.
  const [asked, setAsked] = useUrlParam("tab");
  // ⚠️ «Отгрузки» — экран поставок не-Ozon (файл, склад, заказ 1С). Пока все фиксации — Ozon,
  // вкладка только путает: те же поставки ведутся в «Поставках Ozon» (владелец 15.10.2026). Не
  // удалена — WB через фиксации будет позже. Прячем, только когда список ПРИШЁЛ и в нём нет ни
  // одной поставки не-Ozon: пока он грузится или упал, решать нечем — вкладка остаётся.
  const qc = useQueryClient();
  const runs = useQuery({ queryKey: RUNS_KEY, queryFn: api.getShipmentRuns });
  const shipmentsHidden =
    SUPPLY_UI.ozonSupplies &&
    runs.isSuccess &&
    !runs.data.some((r) => r.non_ozon_count > 0);
  const known: Tab = TABS.includes(asked as Tab) ? (asked as Tab) : "distribution";
  // старая ссылка на скрытые «Отгрузки» ведёт туда, где эти поставки теперь живут
  const tab: Tab = known === "shipments" && shipmentsHidden ? "ozon" : known;
  const setTab = (next: Tab): void => setAsked(next === "distribution" ? "" : next);
  React.useEffect(() => {
    if (known === "shipments" && shipmentsHidden) setAsked("ozon");
    // setAsked меняет идентичность на каждый рендер — в зависимостях ему делать нечего
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [known, shipmentsHidden]);
  // Таблицы тут широкие — ограничение ширины снимаем. «Брони» остаются узкими: это список
  // карточек, а не таблица, и растянутый на весь экран он читается хуже.
  const wide = tab !== "commitments";
  return (
    <div className={cn("mx-auto space-y-5", wide ? "max-w-none" : "max-w-6xl")}>
      <h1 className="text-2xl font-bold">Поставки</h1>
      <div className="flex gap-4 border-b">
        <TabButton active={tab === "distribution"} onClick={() => setTab("distribution")}>
          Распределение
        </TabButton>
        {SUPPLY_UI.productionTab && (
          <TabButton active={tab === "production"} onClick={() => setTab("production")}>
            Производство
          </TabButton>
        )}
        {!shipmentsHidden && (
          <TabButton active={tab === "shipments"} onClick={() => setTab("shipments")}>
            Отгрузки
          </TabButton>
        )}
        {SUPPLY_UI.ozonSupplies && (
          <TabButton active={tab === "ozon"} onClick={() => setTab("ozon")}>
            Поставки Ozon
          </TabButton>
        )}
        {SUPPLY_UI.fbs && (
          <TabButton active={tab === "fbs"} onClick={() => setTab("fbs")}>
            ФБС
          </TabButton>
        )}
        {SUPPLY_UI.poolReserve && (
          <TabButton active={tab === "commitments"} onClick={() => setTab("commitments")}>
            Брони
          </TabButton>
        )}
      </div>
      {tab === "distribution" && (
        <SupplyDistributionPage
          onFixed={async () => {
            // решаем по СВЕЖЕМУ списку: новая фиксация могла добавить поставки не-Ozon
            const fresh = await qc.fetchQuery({
              queryKey: RUNS_KEY,
              queryFn: api.getShipmentRuns,
              staleTime: 0,
            });
            const hidden = SUPPLY_UI.ozonSupplies && !fresh.some((r) => r.non_ozon_count > 0);
            setTab(hidden ? "ozon" : "shipments");
          }}
        />
      )}
      {/* страница цела: скрыта только вкладка (см. features.ts) */}
      {SUPPLY_UI.productionTab && tab === "production" && <SupplyProductionPage />}
      {tab === "shipments" &&
        (runs.isSuccess ? (
          <SupplyShipmentsPage />
        ) : runs.isError ? null : (
          <p className="py-6 text-center text-sm text-muted-foreground">Загрузка…</p>
        ))}
      {SUPPLY_UI.ozonSupplies && tab === "ozon" && <OzonSuppliesPage />}
      {SUPPLY_UI.fbs && tab === "fbs" && <SupplyFbsPage />}
      {SUPPLY_UI.poolReserve && tab === "commitments" && <SupplyCommitmentsPage />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
